import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { MailCheck } from 'lucide-react';
import { signUpSchema, type SignUpInput } from '@auditwire/contracts';
import { Button, Field, Input } from '@/shared/ui';
import { toMessage } from '@/shared/lib/repository/errors';
import { useAuth } from '../useAuth';

export function SignUpForm() {
  const { signUp } = useAuth();
  const [formError, setFormError] = useState('');
  const [submittedEmail, setSubmittedEmail] = useState('');
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<SignUpInput>({
    resolver: zodResolver(signUpSchema),
    defaultValues: { name: '', email: '', password: '', confirmPassword: '' },
  });

  const run = handleSubmit(async (values) => {
    setFormError('');
    try {
      await signUp(values);
      // If the project auto-confirms new users, AuthProvider signs them in and the
      // modal/page reacts to the auth state. Otherwise, prompt them to confirm.
      setSubmittedEmail(values.email);
    } catch (err) {
      setFormError(toMessage(err, 'Could not create your account.'));
    }
  });

  if (submittedEmail) {
    return (
      <div className="space-y-3 py-2 text-center">
        <MailCheck className="mx-auto h-9 w-9 text-amber-500" />
        <p className="font-display text-lg font-bold text-foreground">Check your inbox</p>
        <p className="text-sm text-muted-foreground">
          We sent a confirmation link to <span className="font-medium text-foreground">{submittedEmail}</span>. Confirm
          it, then sign in.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={run} className="space-y-4" noValidate>
      {formError ? (
        <p
          role="alert"
          className="rounded-xl bg-red-50 px-3 py-2 text-sm text-red-600 dark:bg-red-950/40 dark:text-red-400"
        >
          {formError}
        </p>
      ) : null}

      <Field label="Name" htmlFor="signup-name" error={errors.name?.message}>
        <Input id="signup-name" autoComplete="name" placeholder="Your name" {...register('name')} />
      </Field>

      <Field label="Email" htmlFor="signup-email" error={errors.email?.message}>
        <Input
          id="signup-email"
          type="email"
          autoComplete="email"
          placeholder="you@example.com"
          {...register('email')}
        />
      </Field>

      <Field label="Password" htmlFor="signup-password" error={errors.password?.message}>
        <Input
          id="signup-password"
          type="password"
          autoComplete="new-password"
          placeholder="At least 6 characters"
          {...register('password')}
        />
      </Field>

      <Field label="Confirm password" htmlFor="signup-confirm" error={errors.confirmPassword?.message}>
        <Input
          id="signup-confirm"
          type="password"
          autoComplete="new-password"
          placeholder="Re-enter your password"
          {...register('confirmPassword')}
        />
      </Field>

      <Button type="submit" className="w-full" disabled={isSubmitting}>
        {isSubmitting ? 'Creating account…' : 'Create account'}
      </Button>
    </form>
  );
}
